import {
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  Spinner,
  Stack,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useFamilyCard, useUpdateFamilyCard } from "api/users";
import { add, getTime } from "date-fns";
import PageTitle from "generics/PageTitle";
import ToolbarPortal from "generics/ToolbarPortal";
import React, { useRef, useState } from "react";
import { useNavigate, useParams} from "react-router-dom";

export default function FamilyCard() {
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [runsForever, setRunsForever] = useState(false);
  const { id } = useParams();
  const expireRef: any = useRef();
  const discountRef: any = useRef();
  const familyCardQuery = useFamilyCard(id);
  const [updateFamilyCard, updatedFamilyCardInfo] = useUpdateFamilyCard();
  const toast = useToast();

  let familyCard = familyCardQuery.data?.familyCard;
  let user = familyCardQuery.data?.user;

  async function rejectFamilyCard() {
    await updateFamilyCard({
      ...familyCard,
      status: "rejected",
    });
    navigate("/discountcards");
  }

  async function acceptFamilyCard() {
    if (!runsForever && !expireRef.current.value) {
      toast({
        status: "error",
        title: "Invalid Input",
        description: "Please select a valid expiry date",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const discount = parseInt(discountRef?.current?.value);

    if (!discount) {
      toast({
        status: "error",
        title: "Invalid Input",
        description: "Please give valid discount",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (updatedFamilyCardInfo.isLoading) {
      toast({
        status: "info",
        title: "Please Wait",
        description: "Fetching data , try again",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    let discountCardData = {
      ...familyCard,
      status: "accepted",
      expiresAt: runsForever
        ? getTime(add(new Date(), { years: 100 }))
        : new Date(expireRef.current.value).getTime(),
      discount: discount,
      name: user?.name,
    };

    await updateFamilyCard(discountCardData);
    navigate("/discountcards");
  }

  if (familyCardQuery.isLoading) {
    return <Spinner />;
  }

  if (familyCardQuery.status==="success" && !familyCardQuery.data) {
     navigate("/discountcards")
     return null
  }

  return (
    <Box p={12}>
      <ToolbarPortal>
        <Flex justifyContent="space-between" alignItems="center">
          <PageTitle flexGrow={1}>Discount Card</PageTitle>
        </Flex>
      </ToolbarPortal>
      <Stack spacing="8">
        <Text fontSize="xl" fontWeight="medium" color="black.500">
          Discount Card of {user?.name} - {user?.readableId}
        </Text>
        <Stack spacing="8">
          <Text fontSize="md" fontWeight="500" color="black.500">
            Documents
          </Text>
          <Stack direction="row">
            {familyCard?.documents?.map((url: string) => (
              <Image src={url} w="auto" h="300px" />
            ))}
          </Stack>
        </Stack>
        <Divider />
        <Stack direction="row">
          {familyCard?.status !== "accepted" && (
            <Button
              onClick={onOpen}
              isLoading={updatedFamilyCardInfo.isLoading}
            >
              Accept
            </Button>
          )}
          {familyCard?.status !== "rejected" && (
            <Button
              isLoading={updatedFamilyCardInfo.isLoading}
              onClick={rejectFamilyCard}
              colorScheme="red"
            >
              Reject
            </Button>
          )}
        </Stack>
      </Stack>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader
            fontSize="lg"
            fontWeight="bold"
            bg="modalHeader.500"
            color="white"
            borderTopRadius="4px"
            m="0"
          >
            Accept Discount Card
          </ModalHeader>
          <ModalCloseButton color="faded.500" fontSize="14" />
          <ModalBody>
            <Flex flexGrow={1} bg="black">
              <Flex
                bg="black"
                direction="column"
                align-items="flex-start"
                justify="flex-start"
                flex={2}
              >
                <Text pt="2" size="sm" fontWeight="500">
                  Insert Expire Date :
                </Text>
                <Input
                  type="date"
                  name="expire"
                  bg="white"
                  ref={expireRef}
                  isDisabled={runsForever}
                />
              </Flex>
              <Flex direction="column" pl="2" align-items="flex-start" flex={1}>
                <Text pt="2" size="sm" fontWeight="500">
                  Discount :
                </Text>
                <NumberInput
                  defaultValue={20}
                  max={90}
                  type="date"
                  name="start"
                  bg="white"
                >
                  <NumberInputField ref={discountRef} />
                </NumberInput>
              </Flex>
            </Flex>
            <Checkbox
              pt="2"
              colorScheme="primary"
              defaultChecked={runsForever}
              checked={runsForever}
              onChange={(e) => setRunsForever(e.target.checked)}
            >
              Runs Forever
            </Checkbox>
          </ModalBody>
          <Divider></Divider>
          <ModalFooter>
            <Button
              ml="2"
              onClick={onClose}
              background="white"
              color="highlight.500"
              border="solid 2px"
              borderColor="faded.500"
              paddingY="5"
            >
              Close
            </Button>

            <Button
              type="submit"
              ml="2"
              paddingY="5"
              onClick={acceptFamilyCard}
            >
              Submit
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
